import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

const GA_MEASUREMENT_ID = "G-P90HWL120X";
const GA_SCRIPT_ID = "google-analytics-script";
const IDLE_TIMEOUT_MS = 2000;

type Gtag = (...args: unknown[]) => void;
type IdleScheduler = (
  callback: IdleRequestCallback,
  options?: IdleRequestOptions
) => number;
type AnalyticsWindow = Window & {
  dataLayer?: (IArguments | unknown[])[];
  gtag?: Gtag;
};

let isLoadScheduled = false;
let isGtagReady = false;
const pendingPagePaths: string[] = [];

const getAnalyticsWindow = (): AnalyticsWindow => window as AnalyticsWindow;

const sendPageView = (pagePath: string): void => {
  const analyticsWindow = getAnalyticsWindow();
  analyticsWindow.gtag?.("event", "page_view", {
    page_location: `${window.location.origin}${pagePath}`,
    page_path: pagePath,
    send_to: GA_MEASUREMENT_ID,
  });
};

const loadGoogleAnalytics = (): void => {
  if (document.getElementById(GA_SCRIPT_ID)) return;

  const analyticsWindow = getAnalyticsWindow();
  analyticsWindow.dataLayer = analyticsWindow.dataLayer ?? [];
  // gtag.js only executes dataLayer entries that are real `arguments` objects
  // (it checks `[object Arguments]`). A rest-parameter array is silently
  // ignored, which is why this must be a `function` and push `arguments`.
  analyticsWindow.gtag =
    analyticsWindow.gtag ??
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      analyticsWindow.dataLayer?.push(arguments);
    };

  analyticsWindow.gtag("js", new Date());
  analyticsWindow.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
  });

  const script = document.createElement("script");
  script.id = GA_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  isGtagReady = true;
  pendingPagePaths.splice(0).forEach(sendPageView);
};

const scheduleIdleLoad = (): void => {
  const requestIdleCallback = (
    window as unknown as { requestIdleCallback?: IdleScheduler }
  ).requestIdleCallback;

  if (requestIdleCallback) {
    requestIdleCallback(loadGoogleAnalytics, {
      timeout: IDLE_TIMEOUT_MS,
    });
    return;
  }

  setTimeout(loadGoogleAnalytics, 0);
};

const scheduleGoogleAnalytics = (): void => {
  if (isLoadScheduled) return;
  isLoadScheduled = true;

  if (document.readyState === "complete") {
    scheduleIdleLoad();
    return;
  }

  window.addEventListener("load", scheduleIdleLoad, { once: true });
};

const trackPageView = (pagePath: string): void => {
  if (isGtagReady) {
    sendPageView(pagePath);
  } else {
    pendingPagePaths.push(pagePath);
  }

  scheduleGoogleAnalytics();
};

const AnalyticsPageTracker: React.FC = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    trackPageView(`${pathname}${search}`);
  }, [pathname, search]);

  return null;
};

export default AnalyticsPageTracker;
